import { CANDY_APPLE_MEDIUM, EERIE } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import formatName from 'I18n/utils/formatName';
import UIButton from 'UIComponents/button/UIButton';
import UITag from 'UIComponents/tag/UITag';
import { useEmailDeliverabilityStatuses } from 'composer-data/email/public/hooks';
import { validateEmail } from 'composer-data/email/public/operators';
import { HS_EMAIL_ADDRESS } from 'conversations-message-history/common-message-format/constants/deliveryIdentifierTypes';
import { WHATSAPP_GENERIC_CHANNEL_ID } from 'conversations-message-history/common-message-format/constants/genericChannelIds';
import { getRecipientKey, getRecipientsFields } from 'conversations-thread-data/channel-configuration/public/operators';
import { useContactPermissions } from 'conversations-thread-data/contact-permissions/public/hooks';
import { ChannelCapabilitiesConfigContext } from 'conversations-thread-view/channel-capabilities/context/ChannelCapabilitiesConfigContext';
import { List as ImmutableList, Set as ImmutableSet } from 'immutable';
import { useCallback, useContext } from 'react';
import styled from 'styled-components';
import { useWhatsAppSubscription } from '../../../whatsapp/protected/hooks';
import { useUpdateRecipients } from '../hooks/useUpdateRecipients';
import GenericRecipientTag from './GenericRecipientTag';
import { logRecipientChange } from './Recipient';
import { EditorTrackerContext } from '../../../usage-tracking/context/EditorTrackerContext';
import { getCommonMessage } from 'conversations-thread-data/common-reply/public/operators/commonReplyGetters';
import { useNeverLog } from '../../../email/protected/hooks';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledDiv = styled.div.withConfig({
  displayName: "RecipientTagDisplayBar__StyledDiv",
  componentId: "sc-1aqmhql-0"
})(["display:inline-flex;align-items:center;height:100%;width:100%;cursor:pointer;font-weight:normal;font-size:14px;overflow:hidden;color:", ";"], EERIE);
const StyledUITag = styled(UITag).withConfig({
  displayName: "RecipientTagDisplayBar__StyledUITag",
  componentId: "sc-1aqmhql-1"
})(["border:", ";"], ({
  use
}) => use === 'danger' ? `1px solid ${CANDY_APPLE_MEDIUM}` : '');

// Avoid 2 tooltips at the same time when UITag string is shrunk
const truncateStringProps = {
  tooltip: false
};
const MoreButton = ({
  numberOfIdentifiers
}) => {
  return numberOfIdentifiers > 0 ? /*#__PURE__*/_jsx(UIButton, {
    use: "transparent",
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "composer-ui.metadata-editor.email-tag-display-bar.display-more-emails-button",
      options: {
        numberOfEmails: numberOfIdentifiers
      }
    })
  }) : null;
};
MoreButton.displayName = 'MoreButton';
const RecipientTag = ({
  option,
  deleteRecipient,
  validationData,
  isEmailLoggingBlocked
}) => {
  const deliveryIdentifier = option.deliveryIdentifierValue || option.address;
  if (!deliveryIdentifier) {
    return null;
  }
  const name = formatName({
    firstName: option.firstName,
    lastName: option.lastName
  });
  const handleCloseClick = evt => deleteRecipient(evt, deliveryIdentifier);
  const use = validationData.isValid ? 'default' : 'danger';
  return /*#__PURE__*/_jsx(StyledUITag, {
    use: use,
    truncateStringProps: truncateStringProps,
    closeable: true,
    onCloseClick: handleCloseClick,
    children: /*#__PURE__*/_jsx(GenericRecipientTag, {
      option: {
        name,
        address: option.address || undefined,
        value: deliveryIdentifier,
        validationData,
        isEmailLoggingBlocked
      }
    })
  }, deliveryIdentifier);
};
RecipientTag.displayName = 'RecipientTag';
export function RecipientTagDisplayBar({
  allRecipients,
  reply,
  onChange,
  onClick,
  multiCollapseLimit
}) {
  const {
    capabilitiesBlob
  } = useContext(ChannelCapabilitiesConfigContext);
  const commonMessage = getCommonMessage(reply);
  const {
    emailBlocklistStatus
  } = useNeverLog({
    commonMessage
  });
  const recipientFields = getRecipientsFields(capabilitiesBlob);
  const primaryRecipientField = recipientFields && recipientFields[0];
  const primaryRecipientKey = getRecipientKey(primaryRecipientField);
  const primaryRecipients = primaryRecipientKey && allRecipients.get(primaryRecipientKey) || ImmutableList();
  const allRecipientsSize = allRecipients.get('all').size;
  const emailAddresses = allRecipients.get('all').filter(record => record.deliveryIdentifierType === HS_EMAIL_ADDRESS).map(record => record.deliveryIdentifierValue).toArray();
  const shouldDeferEmailSpecific = !emailAddresses.length;
  const {
    deliverabilityStatuses
  } = useEmailDeliverabilityStatuses({
    emailAddresses,
    deferred: shouldDeferEmailSpecific
  });
  const {
    contactPermissions
  } = useContactPermissions({
    emailAddresses: ImmutableSet(emailAddresses),
    deferred: shouldDeferEmailSpecific
  });
  const firstRecipient = primaryRecipients.get(0);
  const deferWhatsAppSubscriptionFetch = !firstRecipient || firstRecipient.genericChannelId !== WHATSAPP_GENERIC_CHANNEL_ID;
  const {
    whatsAppSubscription,
    loading: whatsAppSubscriptionLoading
  } = useWhatsAppSubscription({
    deferred: deferWhatsAppSubscriptionFetch,
    phoneNumber: firstRecipient && firstRecipient.deliveryIdentifierValue
  });
  const getValidationData = record => {
    if (record.deliveryIdentifierType === HS_EMAIL_ADDRESS) {
      const {
        isEmailValid,
        reason
      } = validateEmail(record.deliveryIdentifierValue || record.address || '', deliverabilityStatuses, contactPermissions);
      return {
        isValid: isEmailValid,
        reason
      };
    }
    if (record.genericChannelId === WHATSAPP_GENERIC_CHANNEL_ID) {
      const isOptedInForWhatsApp = !!whatsAppSubscription && whatsAppSubscription.optState === 'OPT_IN';
      const isInvalid = !whatsAppSubscriptionLoading && !isOptedInForWhatsApp;
      return {
        isValid: !isInvalid,
        reason: isInvalid ? 'WHATSAPP_NOT_OPTED_IN' : undefined
      };
    }
    return {
      isValid: true
    };
  };
  const {
    updateRecipients
  } = useUpdateRecipients({
    onChange,
    reply,
    recipientField: primaryRecipientField || null
  });
  const {
    trackEvent
  } = useContext(EditorTrackerContext);
  const deleteRecipient = useCallback((evt, deliveryIdentifierToDelete) => {
    const nextPrimaryRecipients = primaryRecipients.filter(record => record.deliveryIdentifierValue !== deliveryIdentifierToDelete).toList();
    updateRecipients(nextPrimaryRecipients);
    logRecipientChange(nextPrimaryRecipients, primaryRecipients, primaryRecipientKey, trackEvent);

    // if the size of TO recipients is greater than 0, we don't want the
    // click event to propagate to EmailTagDisplayBar and expand all
    // fields. However when the size is 0 we want to expand the field and reveal
    // other recipients if any. Otherwise this results in collapsed metadata
    // displaying just '+x more' text without any recipient names
    if (nextPrimaryRecipients.size > 0) {
      evt.stopPropagation();
      evt.preventDefault();
    }
  }, [primaryRecipients, updateRecipients, primaryRecipientKey, trackEvent]);
  const otherPrimaryRecipientCount = Math.max(primaryRecipients.size - multiCollapseLimit, 0);
  const numberOfIdentifiers = allRecipientsSize - (primaryRecipients.size - otherPrimaryRecipientCount);
  return /*#__PURE__*/_jsxs(StyledDiv, {
    onClick: onClick,
    children: [allRecipientsSize ? primaryRecipients.slice(0, multiCollapseLimit).map(record => /*#__PURE__*/_jsx(RecipientTag, {
      option: record,
      validationData: getValidationData(record),
      deleteRecipient: deleteRecipient,
      isEmailLoggingBlocked: emailBlocklistStatus === null || emailBlocklistStatus === void 0 ? void 0 : emailBlocklistStatus[record.deliveryIdentifierValue]
    }, record.deliveryIdentifierValue)) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "composer-ui.metadata-editor.email-tag-display-bar.no-recipients"
    }), /*#__PURE__*/_jsx(MoreButton, {
      numberOfIdentifiers: numberOfIdentifiers
    })]
  });
}
RecipientTagDisplayBar.displayName = 'RecipientTagDisplayBar';